import { useEffect, useState } from "react";
import { api, API } from "./api";
import { useSiteSettings } from "./settings";

/**
 * useRealisations — fetches realisations from backend API.
 * Returns { list, loading, error, refresh }.
 */
export function useRealisations() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/realisations");
      setList(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  return { list, loading, error, refresh: load };
}

/** Resolve image URL — supports relative /api/files/... paths AND absolute URLs.
 *  For Unsplash URLs, automatically appends &fm=webp for ~30% smaller transfers. */
export const resolveImage = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) {
    if (src.includes("images.unsplash.com") && !src.includes("fm=")) {
      return src + (src.includes("?") ? "&" : "?") + "fm=webp";
    }
    return src;
  }
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

// Defaults used until settings are fetched.
const DEFAULT_MISSIONS = [
  { key: "economie", label: "Économie" },
  { key: "moex", label: "MOEx" },
  { key: "opc", label: "OPC" },
];
const DEFAULT_CATEGORIES = [
  { key: "logements", label: "Logements" },
  { key: "scolaire", label: "Scolaire" },
  { key: "tertiaire", label: "Tertiaire" },
  { key: "equipements", label: "Équipements publics" },
];

// Exported defaults for backward compatibility
export const MISSIONS = DEFAULT_MISSIONS;
export const CATEGORIES = DEFAULT_CATEGORIES;

/** Hook returning dynamic categories/missions configured in /admin. */
export function useTaxonomies() {
  const { settings } = useSiteSettings();
  const categories = settings?.realisation_categories?.length ? settings.realisation_categories : DEFAULT_CATEGORIES;
  const missions = settings?.realisation_missions?.length ? settings.realisation_missions : DEFAULT_MISSIONS;
  return { categories, missions };
}
